import { createApi, retry } from "@reduxjs/toolkit/query/react";
import { baseQuery } from "api";
import { CategoryList, LotteryListModel, LotteryModel } from "models";
import { ApiRoute } from "utils";

const baseQueryWithRetry = retry(baseQuery, { maxRetries: 0 });

// const DEFAULT_STATUS = " ";
const DEFAULT_PAGE = " ";
const DEFAULT_KEYWORD = " ";
const DEFAULT_CATE_ID = " ";

const validateStatus = (response: Response, result: any) =>
  response.status === 200 && !result.isError;

export const lotteryApi = createApi({
  reducerPath: "lotteryApi",
  baseQuery: baseQueryWithRetry,
  keepUnusedDataFor: 0,
  refetchOnMountOrArgChange: true,
  refetchOnFocus: true,
  refetchOnReconnect: true,
  endpoints: (build) => ({
    getLotteries: build.query<
      LotteryListModel,
      {
        pageArg: number;
        statusArg?: number | string;
        keywordArg?: string;
        cateIdArg?: string;
      }
    >({
      query: (arg) => {
        const { pageArg, keywordArg, cateIdArg } = arg;
        return {
          url: `${ApiRoute.LOTTERY_INDEX}?page=${
            pageArg || DEFAULT_PAGE
          }&keyword=${keywordArg || DEFAULT_KEYWORD}&category_id=${
            cateIdArg || DEFAULT_CATE_ID
          }`,
          validateStatus: validateStatus,
        };
      },
      transformResponse: (response: { data: LotteryListModel }) => {
        return response.data;
      },
    }),
    getLottery: build.query<LotteryModel, number>({
      query: (id) => ({
        url: `${ApiRoute.LOTTERY_DETAIL}/${id}`,
        validateStatus: validateStatus,
      }),
      transformResponse: (response: { data: LotteryModel }) => {
        return response.data;
      },
    }),
    getLotteryCategories: build.query({
      query: () => {
        return {
          url: `${ApiRoute.CATEGORIES}`,
          validateStatus: validateStatus,
        };
      },
      transformResponse: (response: { data: CategoryList }) => {
        return response.data;
      },
    }),
    wishReceivePrizes: build.mutation<
      any,
      { order_id: number; prize_ids_to_mail: number[] }
    >({
      query: (args) => {
        return {
          url: `${ApiRoute.REDEEM_POINTS}`,
          method: "POST",
          body: {
            order_id: args.order_id,
            prize_ids_to_mail: args.prize_ids_to_mail,
          },
        };
      },
      transformResponse: (response: any) => {
        return response;
      },
    }),
    exchangePrizeToPoint: build.mutation<any, { purchased_lottery_id: number }>(
      {
        query: (args) => {
          return {
            url: `${ApiRoute.REDEEM_ALL_POINTS}`,
            method: "POST",
            body: { purchased_lottery_id: args.purchased_lottery_id },
          };
        },
        transformResponse: (response: any) => {
          return response;
        },
      }
    ),
    wishReceivePrize: build.mutation<
      any,
      { order_id: number; desired_prize_type: string }
    >({
      query: (args) => {
        return {
          url: `${ApiRoute.RECEIVED_PRIZE_MAIL}`,
          method: "POST",
          body: {
            order_id: args.order_id,
            desired_prize_type: args.desired_prize_type,
          },
        };
      },
      transformResponse: (response: any) => {
        return response;
      },
    }),

    withDrawPoint: build.mutation<any, { points: number }>({
      query: (args) => {
        return {
          url: `${ApiRoute.WITHDRAW_POINTS}`,
          method: "POST",
          body: {
            points: args.points,
          },
        };
      },
      transformResponse: (response: any) => {
        return response;
      },
    }),
  }),
});

export const {
  useGetLotteryQuery,
  useGetLotteriesQuery,
  useGetLotteryCategoriesQuery,
  useExchangePrizeToPointMutation,
  useWishReceivePrizeMutation,
  useWishReceivePrizesMutation,
  useWithDrawPointMutation,
} = lotteryApi;
